import {makeStyles} from '@material-ui/core';
import {Colors} from '../../../../styles/Colors';
import {TFunction} from 'react-i18next';
import {CallHistory} from '../../../../store/types/CallHistory';
import {Column} from 'react-table';
import {prepareCallRecordName} from '../../../../utils/calls/prepareCallRecordName';
import {Assignment, CloudDownload} from '@material-ui/icons';
import React from 'react';
import {ChangeDirtyStateDetailsTab} from '../../../../views/Extensions/ExtensionDetails/utils';
import DateTimeCell from '../../../Calls/DateTimeCell';
import CallerInfo from '../../../Calls/CallerInfo';
import CustomizedIconButton from '../../../IconButton/IconButton';
import PermissionProvider from '../../../PermissionProvider/PermissionProvider';

export type CallHistoryFormProps = {
    from: string;
    till: string;
    cli: string;
    cld: string;
    unsuccessfulCalls: boolean;
};
export const dateFormat = 'YYYY-MM-DD HH:mm:ss';

export interface CallRecordingProps {
    accountId?: number;
    handleDirtyChange?: ChangeDirtyStateDetailsTab;
    handleSetSubmitFunc?: (funcName: string, func: () => void) => void;
    handleSetIsValidFunc?: (funcName: string, func: () => boolean) => void;
    downloadPermission?: string[];
    deletePermission?: string[];
    recordPermission?: string[];
    downloadTranscriptionPermission?: string[];
    playPermission?: string[];
}
export const useStyles = makeStyles(() => ({
    mainContainer: {
        maxWidth: 1040,
        padding: '22px 16px',
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
    },
    titleContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginRight: 25,

        '& h2': {
            fontSize: 32,
            lineHeight: '24px',
            marginBlockEnd: 8,
            marginBlockStart: 4,
            fontWeight: 500,
        },
        '& p': {
            fontSize: 11,
            lineHeight: '16px',
            opacity: 0.38,
            marginBlockStart: 0,
        },
    },
    filtersContainer: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 990,
    },
    itemsContainer: {
        marginTop: 10,
        display: 'flex',
        alignItems: 'flex-start',
    },
    tableContainer: {
        maxWidth: 1040,

        '& .MuiTableRow-root': {
            padding: '0 20px',
        },
    },
    inputsContainer: {
        display: 'flex',
        alignItems: 'flex-start',

        '& .MuiFormControl-root': {
            marginRight: 18,
        },

        '& .date-time-picker': {
            '& .MuiInputBase-input': {
                width: 208,
            },
        },
    },
    inputs: {
        '& .MuiFormControl-root:first-of-type': {
            marginRight: 90,
        },
    },
    button: {
        '& .MuiButton-label': {
            color: Colors.White,
        },
        minWidth: 96,
        marginTop: 8,
    },
    table: {
        marginTop: 30,
    },
    pagination: {
        color: Colors.Gray,
        marginTop: 30,
    },
    centeredChargeValue: {
        paddingLeft: '0!important',
        marginLeft: 30,
    },
    centeredRecordingValue: {
        paddingLeft: '0!important',
        marginLeft: 20,
    },
    rowBox: {
        padding: 0 + '!important',
        display: 'flex',
        alignItems: 'center',
    },
    rowBoxHeader: {
        marginRight: 20,
        fontSize: 16,
        fontWeight: 700,
        color: Colors.Gray7,
    },
    container: {
        '& .MuiPaper-root': {
            width: 336,
        },

        '& .MuiDialogContent-root': {
            minWidth: 'unset !important',
            padding: '24px 24px 26px 24px !important',
            background: `${Colors.White} !important`,
        },
        '& .MuiDialogActions-root': {
            '& > :first-child': {
                marginRight: 20,
            },
        },
    },
    primaryModalButton: {
        '& .MuiButton-label': {
            color: Colors.Primary,
        },
    },
    checkbox: {
        padding: '5px 0 23px 0 !important',
        marginTop: 15
    },
    recordingColumn: {
        display: 'flex',
        marginLeft: -20,
    },
    cloud: {
        display: 'flex',
        alignItems: 'center',
        color: Colors.LightGraySecondary3,
        marginLeft: 20,
        minWidth: 30,
    },
    tooltipContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '4px 8px',
    },
    tooltip: {
        display: 'flex',
        flex: 1,
        alignItems: 'center',
    },
    tooltipTitle: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        fontSize: '16px',
        lineHeight: '28px',
        marginRight: '8px',
        whiteSpace: 'pre-wrap',
    },
    chargeUSD: {
        display: 'flex',
        justifyContent: 'center',
        minWidth: 130,
    },
    download: {
        marginLeft: 20,
        color: `${Colors.PlaceHolder} !important`,
    },
    duration: {
        display: 'block',
        width: '100%',
        textAlign: 'right',
    },
}));

export const generateColumns = (
    t: TFunction<string>,
    classes: ReturnType<typeof useStyles>,
    timezoneOffset: number,
    customerCurrency: string,
    downloadFile: (
        i_xdr: number,
        call_recording_id: string,
        fileName: string,
        index: number,
    ) => void,
    setCallRecordInformation: React.Dispatch<
        React.SetStateAction<CallHistory | undefined>
    >,
    userDateTimeFormat?: string,
    isDownloadingGoingOn?: boolean[],
    isDialogOpened?: boolean,
    downloadPermission?: string[],
    recordPermission?: string[],
): Column<CallHistory>[] => {
    return [
        {
            accessor: 'connect_time',
            Header: t<string>('screens:calls.connectTime'),
            width: 1.3,
            Cell: function Cell(params) {
                return (
                    <DateTimeCell
                        timezoneOffset={timezoneOffset}
                        time={params.row.original.connect_time as string}
                        customDateFormat={userDateTimeFormat}
                    />
                );
            },
        },
        {
            accessor: 'CLI',
            Header: t<string>('screens:calls.caller'),
            width: 1,
            Cell: function Cell(params) {
                if(params.row.original?.CLI)
                return <CallerInfo caller={params.row.original.CLI} />;
                return <></>;
            },
        },
        {
            accessor: 'CLD',
            Header: t<string>('screens:calls.destination'),
            width: 1.75,
            Cell: function Cell(params) {
                if(params.row.original.CLD)
                return <CallerInfo caller={params.row.original.CLD} />;
                return <></>;
            },
        },
        {
            accessor: 'charged_quantity',
            Header: (
                <span className={classes.duration}>
                    {t<string>('screens:calls.duration')}
                </span>
            ),
            width: 0.8,
            Cell: function Cell(params) {
                return (
                    <span className={classes.duration}>
                        {params.row.original?.charged_quantity
                        ? new Date(params.row.original.charged_quantity * 1000)
                            .toISOString()
                            .substr(11, 8)
                        : ''}
                    </span>
                );
            },
        },
        {
            accessor: 'charged_amount',
            Header:
                t('screens:calls.charge') +
                (customerCurrency ? `, ${customerCurrency}` : ''),
            width: 1,
            Cell: function Cell(params) {
                return (
                    <span className={classes.chargeUSD}>
                        {' '}
                        {params.row.original.charged_amount?.toFixed(2)}
                    </span>
                );
            },
        },
        {
            accessor: 'cr_download_ids',
            Header: t<string>('common:actions'),
            maxWidth: 100,
            minWidth: 100,
            width: 1,
            Cell: function Cell(params) {
                const number = params.row.original.i_xdr as number;
                const downloadId = params.row.original.cr_download_ids?.[0];
                const index = params.row.index;
                const row = params.row.original;

                return (
                    <div className={classes.recordingColumn}>
                        <PermissionProvider permission={recordPermission || []} skipReadOnly>
                            <CustomizedIconButton
                                tooltipText={t(
                                    'tooltips:callSettings.viewCallDetailRecord',
                                )}
                                dataQa="details-icon-button"
                                dataTestId="details-icon-button"
                                onClick={() =>
                                    setCallRecordInformation(
                                        params.row.original,
                                    )
                                }
                            >
                                <Assignment />
                            </CustomizedIconButton>
                        </PermissionProvider>

                        {downloadId && (
                            <PermissionProvider
                                permission={downloadPermission || []} skipReadOnly
                            >
                                <CustomizedIconButton
                                    tooltipText={t(
                                        'tooltips:ringGroups.downloadRecording',
                                    )}
                                    dataQa="detail-download-icon"
                                    dataTestId="download-icon"
                                    onClick={() => 
                                        downloadFile(
                                            number,
                                            downloadId,
                                            prepareCallRecordName(
                                                row,
                                                timezoneOffset,
                                            ),
                                            index,
                                        )
                                    }
                                    disabled={
                                        !(
                                            downloadId &&
                                            !isDownloadingGoingOn?.[
                                                params.row.index
                                            ]
                                        )
                                    }
                                >
                                    <CloudDownload />
                                </CustomizedIconButton>
                            </PermissionProvider>
                        )}
                    </div>
                );
            },
        },
    ];
};
